import { create } from 'zustand';
import type { HistoricoArete } from '../../api-client/pre-compras/aretes.dto';
import {
  obtenerHistorialAreteSiiniga,
  obtenerHistorialAreteTemporal
} from '../../api-client/pre-compras/pre-compras.api';
import { makeGet, makePatch } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import { type SortDir } from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Compra, CompraBusqueda } from './compra';

export interface CompraState {
  list: PaginatedList<CompraBusqueda>;
  current: Compra;
  forceReload: boolean;
  historialSiiniga: Array<HistoricoArete>;
  historialTemporal: Array<HistoricoArete>;

  nueva: () => void;
  cargarPaginadas: (params: ComprasPaginadasQueryParams) => Promise<void>;
  cargarPorId: (id: string) => Promise<void>;
  cargarHistorialSiiniga: (folio: string, partida: number) => Promise<void>;
  cargarHistorialTemporal: (folio: string, partida: number) => Promise<void>;
  convertir: (id: string, request: ConvertirACompraRequest) => Promise<boolean>;
  actualizar: (id: string, request: ActualizarCompraRequest) => Promise<boolean>;
  cancelar: (id: string) => Promise<boolean>;
}

export interface ConvertirACompraRequest {
  partidas: Array<PartidaConversionDto>;
}

export interface ActualizarCompraRequest {
  fecha: Date;
  referencia: string;
  idPersonaVende: number;
  idPersonaRecibe: number;
  idPersonaEntrega: number;
  idCorral: number;
}

export interface PartidaConversionDto {
  partida: number;
  omiteDescuento: boolean;
  precio: number;
}

export interface ComprasPaginadasQueryParams {
  fechaDesde: Date;
  fechaHasta: Date;
  filtro: string;
  tipoDoc: string;
  columnaFiltro: string;
  columnaOrden: string;
  direccionOrden: SortDir;
  limite: number;
  pagina: number;
}

function toQueryString(params: ComprasPaginadasQueryParams) {
  let {
    fechaDesde,
    fechaHasta,
    columnaFiltro,
    columnaOrden,
    direccionOrden,
    filtro,
    pagina,
    limite
  } = params;

  columnaFiltro ??= 'nombre';
  columnaOrden ??= 'clave';
  direccionOrden ??= 'asc';
  filtro ??= '';
  pagina ??= 0;
  limite ??= 10;

  let queryString =
    `fechaDesde=${fechaDesde.toISOString()}&fechaHasta=${fechaHasta.toISOString()}` +
    `&columnaFiltro=${columnaFiltro}&tipoDocumento=${params.tipoDoc}&columnnaOrden=${columnaOrden}` +
    `&direccionOrden=${direccionOrden}&filtro=${filtro}&pagina=${pagina}&limite=${limite}`;

  return queryString;
}

const { showSuccessToast } = useUIStore.getState();

export const useComprasStore = create<CompraState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: { activa: true } as Compra,
  forceReload: false,
  historialSiiniga: [] as Array<HistoricoArete>,
  historialTemporal: [] as Array<HistoricoArete>,

  nueva: () => {
    set({
      current: {} as Compra,
      historialSiiniga: [],
      historialTemporal: []
    });
  },
  cargarPaginadas: async (params: ComprasPaginadasQueryParams) => {
    const data = await makeGet<PaginatedList<CompraBusqueda>>(
      `/compras/paginadas?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarPorId: async (id: string) => {
    const data = await makeGet<Compra>(`/compras/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result,
      forceReload: false
    });
  },
  cargarHistorialSiiniga: async (folio: string, partida: number) => {
    const response = await obtenerHistorialAreteSiiniga(folio, partida);

    if (response.isFailure) return;

    set({
      historialSiiniga: response.result
    });
  },
  cargarHistorialTemporal: async (folio: string, partida: number) => {
    const response = await obtenerHistorialAreteTemporal(folio, partida);

    if (response.isFailure) return;

    set({
      historialTemporal: response.result
    });
  },
  convertir: async (id: string, request: ConvertirACompraRequest) => {
    const data = await makePatch<ConvertirACompraRequest, Compra>(
      `/compras/${id}/convertir`,
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Conversión a Compra registrada');

    const current = get().current;

    set({
      current: {
        ...current,
        ...{
          partidas: current.partidas.map((p, i) => ({
            ...p,
            precio: request.partidas[i - 1]?.precio ?? p.precio
          })),
          esCompra: true,
          tipo: 'COMPRA'
        }
      },
      forceReload: true
    });

    return true;
  },
  actualizar: async (id: string, request: ActualizarCompraRequest) => {
    const data = await makePatch<ActualizarCompraRequest, Compra>(`/compras/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Compra actualizada');

    set({
      current: {
        ...get().current,
        ...{
          referencia: request.referencia,
          idCorral: request.idCorral,
          idPersonaEntrega: request.idPersonaEntrega,
          idPersonaRecibe: request.idPersonaRecibe,
          idPersonaVende: request.idPersonaVende,
          fecha: request.fecha
        }
      }
    });

    return true;
  },
  cancelar: async (id: string) => {
    const data = await makePatch(`/compras/${id}/cancelar`);

    if (data.isFailure) return false;

    showSuccessToast('Compra cancelada');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
